import { useEffect, useState } from 'react';
import { Alert, Modal } from 'antd';
import { MIN_REASON, ReasonInput, reasonOk } from 'pages/access-groups/common';
import { onOff } from './matrix';
import { DraftChange } from './types';
import styles from './styles.module.scss';

interface SaveProps {
  open: boolean;
  changes: DraftChange[];
  saving: boolean;
  error: string;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}

/** "Lưu…": liệt kê từng thay đổi bằng lời, bắt nhập lý do rồi mới gửi. */
export const SaveModal = ({ open, changes, saving, error, onCancel, onSubmit }: SaveProps) => {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  return (
    <Modal
      open={open}
      width={600}
      title={`Lưu ${changes.length} thay đổi quyền`}
      okText="Lưu thay đổi"
      cancelText="Xem lại"
      okButtonProps={{ disabled: !reasonOk(reason) || !changes.length }}
      confirmLoading={saving}
      maskClosable={false}
      onCancel={saving ? undefined : onCancel}
      onOk={() => onSubmit(reason.trim())}
      destroyOnHidden
    >
      <p style={{ marginBottom: 8 }}>
        Các thay đổi dưới đây có tác dụng ngay sau khi lưu, được ghi vào nhật ký và báo cho các
        Admin khác:
      </p>
      <ol className={styles.changeList}>
        {changes.map((change) => (
          <li key={change.key}>
            {[change.tierLabel, change.actionLabel, change.scopeLabel].filter(Boolean).join(' · ')}:{' '}
            <span className={styles.changeFrom}>{onOff(!change.allowed)}</span> →{' '}
            <span className={styles.changeTo}>{onOff(change.allowed)}</span>
          </li>
        ))}
      </ol>
      <ReasonInput
        id="perm-save-reason"
        label={`Lý do đổi quyền (bắt buộc, ít nhất ${MIN_REASON} ký tự)`}
        value={reason}
        onChange={setReason}
        disabled={saving}
        placeholder="Ví dụ: Quản lý vận hành không còn phụ trách ghi phạt từ tháng 10."
      />
      {error && <Alert type="error" showIcon style={{ marginTop: 8 }} message={error} />}
    </Modal>
  );
};

interface ResetProps {
  open: boolean;
  changedCount: number;
  draftCount: number;
  saving: boolean;
  error: string;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}

/** "Khôi phục mặc định": đưa mọi ô về mặc định, cũng phải có lý do. */
export const ResetModal = ({
  open,
  changedCount,
  draftCount,
  saving,
  error,
  onCancel,
  onSubmit,
}: ResetProps) => {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  return (
    <Modal
      open={open}
      width={560}
      title="Khôi phục quyền mặc định"
      okText="Khôi phục mặc định"
      cancelText="Huỷ"
      okButtonProps={{ danger: true, disabled: !reasonOk(reason) }}
      confirmLoading={saving}
      maskClosable={false}
      onCancel={saving ? undefined : onCancel}
      onOk={() => onSubmit(reason.trim())}
      destroyOnHidden
    >
      <p style={{ marginBottom: 8 }}>
        Mọi ô trong bảng quyền sẽ trở về mặc định.{' '}
        {changedCount > 0
          ? `Hiện có ${changedCount} ô đang khác mặc định, các ô này sẽ bị đổi lại.`
          : 'Hiện chưa có ô nào khác mặc định.'}
      </p>
      {draftCount > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${draftCount} thay đổi chưa lưu trên màn hình này cũng sẽ bị bỏ.`}
        />
      )}
      <ReasonInput
        id="perm-reset-reason"
        label={`Lý do khôi phục (bắt buộc, ít nhất ${MIN_REASON} ký tự)`}
        value={reason}
        onChange={setReason}
        disabled={saving}
      />
      {error && <Alert type="error" showIcon style={{ marginTop: 8 }} message={error} />}
    </Modal>
  );
};
